/**
 * SIP Manipulations Page
 * 
 * Manage SIP manipulation profiles for transforming SIP messages.
 * Follows the established pattern from SipConfigurationPage.
 */

import React, { useState } from 'react';
import { 
  Plus, 
  CheckCircle, 
  Settings,
  X,
  XCircle,
  AlertTriangle,
  Wand2,
  Hash,
  Copy,
  TestTube2,
  RefreshCw
} from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import type { Sip } from '@sbclite/core';
import { 
  useSipManipulations, 
  useDeleteSipManipulation,
  useToggleSipManipulation,
  useSipManipulationUsage
} from '../../hooks/useSipManipulations';
import { ManipulationEditor } from '../../components/sip/ManipulationEditor';
import { ManipulationTester } from '../../components/sip/ManipulationTester';
import { useViewMode } from '../../hooks/useViewMode';
import {
  PageHeader,
  StatsGrid,
  StatCard,
  FilterBar,
  ItemActions,
  EmptyState,
  LoadingState,
  ErrorState,
  DeleteConfirmModal,
} from '../../components/common';

// ========================================
// TYPES
// ========================================

type FilterType = 'all' | 'enabled' | 'disabled';

// ========================================
// HELPER FUNCTIONS
// ========================================

function getTargetColor(target: string): string {
  const colors: Record<string, string> = {
    'From': 'bg-blue-100 text-blue-800',
    'To': 'bg-green-100 text-green-800',
    'Request-URI': 'bg-purple-100 text-purple-800',
    'P-Asserted-Identity': 'bg-orange-100 text-orange-800',
    'Remote-Party-ID': 'bg-pink-100 text-pink-800',
    'Contact': 'bg-cyan-100 text-cyan-800',
    'Via': 'bg-gray-100 text-gray-800'
  };
  return colors[target] || 'bg-gray-100 text-gray-800';
}

function getActionColor(action: string): string {
  const colors: Record<string, string> = {
    'replace': 'bg-yellow-100 text-yellow-800',
    'prefix': 'bg-green-100 text-green-800',
    'suffix': 'bg-blue-100 text-blue-800',
    'strip': 'bg-red-100 text-red-800',
    'set': 'bg-purple-100 text-purple-800'
  };
  return colors[action] || 'bg-gray-100 text-gray-800';
}

// ========================================
// MAIN COMPONENT
// ========================================

export function SipManipulationsPage() {
  const queryClient = useQueryClient();
  
  // State management - use user preference for default view mode
  const [viewMode, setViewMode] = useViewMode();
  const [filterType, setFilterType] = useState<FilterType>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showEditor, setShowEditor] = useState(false);
  const [showTester, setShowTester] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState<Sip.ManipulationInfo | null>(null);
  const [profileToDelete, setProfileToDelete] = useState<Sip.ManipulationInfo | null>(null);
  const [editingProfile, setEditingProfile] = useState<Sip.ManipulationInfo | null>(null);

  // API queries
  const manipulationsQuery = useSipManipulations();
  const deleteProfileMutation = useDeleteSipManipulation();
  const toggleEnabledMutation = useToggleSipManipulation();

  const manipulations = manipulationsQuery.data?.data || [];

  // Filter and search logic
  const filteredProfiles = manipulations.filter(profile => {
    // Type filter
    let matchesType = false;
    if (filterType === 'all') {
      matchesType = true;
    } else if (filterType === 'enabled') {
      matchesType = profile.enabled === true;
    } else if (filterType === 'disabled') {
      matchesType = profile.enabled === false;
    }

    // Search filter
    const matchesSearch = searchQuery === '' || 
      profile.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      profile.description?.toLowerCase().includes(searchQuery.toLowerCase());

    return matchesType && matchesSearch;
  });

  // Sort profiles by name
  const sortedProfiles = [...filteredProfiles].sort((a, b) => {
    return a.name.localeCompare(b.name);
  });

  // Calculate statistics
  const stats = {
    total: manipulations.length,
    enabled: manipulations.filter(p => p.enabled === true).length,
    disabled: manipulations.filter(p => p.enabled === false).length,
    totalRules: manipulations.reduce((sum, p) => sum + p.rules.length, 0)
  };

  // Handlers
  const handleDeleteProfile = (profile: Sip.ManipulationInfo) => {
    setProfileToDelete(profile);
    setShowDeleteModal(true);
  };

  const confirmDeleteProfile = () => {
    if (profileToDelete) {
      deleteProfileMutation.mutate(profileToDelete.id, {
        onSuccess: () => {
          setShowDeleteModal(false);
          setProfileToDelete(null);
        },
        onError: (error: Error) => {
          alert(`Failed to delete profile: ${error.message}`);
        }
      });
    }
  };

  const cancelDeleteProfile = () => {
    setShowDeleteModal(false);
    setProfileToDelete(null);
  };

  const handleEditProfile = (profile: Sip.ManipulationInfo) => {
    setEditingProfile(profile);
    setShowEditor(true);
  };

  const handleToggleEnabled = (profile: Sip.ManipulationInfo) => {
    toggleEnabledMutation.mutate({ id: profile.id, enabled: !profile.enabled });
  };

  const handleTestProfile = (profile: Sip.ManipulationInfo) => {
    setSelectedProfile(profile);
    setShowTester(true);
  };

  // Utility functions
  const getStatusColor = (enabled: boolean) => {
    return enabled 
      ? 'bg-green-100 text-green-800 border-green-200'
      : 'bg-gray-100 text-gray-800 border-gray-200';
  };

  // Filter options
  const filterOptions = [
    { value: 'all', label: 'All Profiles' },
    { value: 'enabled', label: 'Enabled' },
    { value: 'disabled', label: 'Disabled' },
  ];

  if (manipulationsQuery.isLoading) {
    return <LoadingState />;
  }

  if (manipulationsQuery.error) {
    return (
      <ErrorState
        title="Failed to load SIP manipulation profiles"
        message={manipulationsQuery.error instanceof Error ? manipulationsQuery.error.message : 'Unknown error'}
        onRetry={() => manipulationsQuery.refetch()}
      />
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <PageHeader
        title="SIP Manipulations"
        description="Manage reusable SIP message transformation profiles"
        onRefresh={() => manipulationsQuery.refetch()}
        isRefreshing={manipulationsQuery.isFetching}
        primaryAction={{
          label: 'Add Profile',
          icon: Plus,
          onClick: () => {
            setEditingProfile(null);
            setShowEditor(true);
          },
        }}
      />

      {/* Statistics Cards */}
      <StatsGrid>
        <StatCard
          label="Total Profiles"
          value={stats.total}
          icon={Wand2}
          iconBgColor="bg-blue-100"
          iconColor="text-blue-600"
        />
        <StatCard
          label="Enabled"
          value={stats.enabled}
          icon={CheckCircle}
          iconBgColor="bg-green-100"
          iconColor="text-green-600"
          valueColor="text-green-600"
        />
        <StatCard
          label="Disabled"
          value={stats.disabled}
          icon={XCircle}
          iconBgColor="bg-gray-100"
          iconColor="text-gray-600"
        />
        <StatCard
          label="Total Rules"
          value={stats.totalRules}
          icon={Hash}
          iconBgColor="bg-purple-100"
          iconColor="text-purple-600"
          valueColor="text-purple-600"
        />
      </StatsGrid>

      {/* Filters and Search */}
      <FilterBar
        searchPlaceholder="Search profiles..."
        searchValue={searchQuery}
        onSearchChange={setSearchQuery}
        filterOptions={filterOptions}
        filterValue={filterType}
        onFilterChange={(value) => setFilterType(value as FilterType)}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
      />

      {/* Profiles Display */}
      {sortedProfiles.length === 0 ? (
        <EmptyState
          icon={Wand2}
          title="No manipulation profiles found"
          description="Get started by creating a manipulation profile."
          isFiltered={!!(searchQuery || filterType !== 'all')}
          action={{
            label: 'Create Profile',
            icon: Plus,
            onClick: () => setShowEditor(true),
          }}
        />
      ) : viewMode === 'grid' ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {sortedProfiles.map((profile) => (
            <ManipulationProfileCard
              key={profile.id}
              profile={profile}
              onView={() => {
                setSelectedProfile(profile);
                setShowDetailsModal(true);
              }}
              onEdit={() => handleEditProfile(profile)}
              onTest={() => handleTestProfile(profile)}
              onToggle={() => handleToggleEnabled(profile)}
              onDelete={() => handleDeleteProfile(profile)}
              isToggling={toggleEnabledMutation.isPending}
            />
          ))}
        </div>
      ) : (
        // List View
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Profile</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rules</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Targets</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {sortedProfiles.map((profile) => {
                const uniqueTargets = [...new Set(profile.rules.map(r => r.target))];
                
                return (
                  <tr key={profile.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex flex-col">
                        <div className="text-sm font-medium text-gray-900">{profile.name}</div>
                        {profile.description && (
                          <div className="text-xs text-gray-500 truncate max-w-xs">{profile.description}</div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                        {profile.rules.length} rules
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex flex-wrap gap-1">
                        {uniqueTargets.slice(0, 3).map((target, idx) => (
                          <span
                            key={idx}
                            className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getTargetColor(target)}`}
                          >
                            {target}
                          </span>
                        ))}
                        {uniqueTargets.length > 3 && (
                          <span className="text-xs text-gray-500">+{uniqueTargets.length - 3}</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getStatusColor(profile.enabled)}`}>
                        {profile.enabled ? 'Enabled' : 'Disabled'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <ItemActions
                        variant="table"
                        actions={[
                          {
                            kind: 'view',
                            onClick: () => {
                              setSelectedProfile(profile);
                              setShowDetailsModal(true);
                            },
                          },
                          { kind: 'test', onClick: () => handleTestProfile(profile) },
                          { kind: 'edit', onClick: () => handleEditProfile(profile) },
                          {
                            kind: profile.enabled ? 'disable' : 'enable',
                            onClick: () => handleToggleEnabled(profile),
                            disabled: toggleEnabledMutation.isPending,
                          },
                          { kind: 'delete', onClick: () => handleDeleteProfile(profile) },
                        ]}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Manipulation Editor Modal */}
      {showEditor && (
        <ManipulationEditor
          profile={editingProfile}
          onClose={() => {
            setShowEditor(false);
            setEditingProfile(null);
          }}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['sip-manipulations'] });
            setShowEditor(false);
            setEditingProfile(null);
          }}
        />
      )}

      {/* Manipulation Tester Modal */}
      {showTester && selectedProfile && (
        <ManipulationTester
          profile={selectedProfile}
          onClose={() => {
            setShowTester(false);
            setSelectedProfile(null);
          }}
        />
      )}

      {/* Profile Details Modal */}
      {showDetailsModal && selectedProfile && (
        <ProfileDetailsModal
          profile={selectedProfile}
          onClose={() => {
            setShowDetailsModal(false);
            setSelectedProfile(null);
          }}
          onEdit={() => {
            setShowDetailsModal(false);
            handleEditProfile(selectedProfile);
          }}
          onTest={() => {
            setShowDetailsModal(false);
            handleTestProfile(selectedProfile);
          }}
        />
      )}

      {/* Delete Confirmation Modal */}
      <DeleteConfirmModal
        isOpen={showDeleteModal && !!profileToDelete}
        onClose={cancelDeleteProfile}
        onConfirm={confirmDeleteProfile}
        title="Delete Manipulation Profile"
        entityType="manipulation profile"
        entityName={profileToDelete?.name || ''}
        additionalInfo={profileToDelete && (
          <ManipulationUsageInfo profileId={profileToDelete.id} rulesCount={profileToDelete.rules.length} />
        )}
        isDeleting={deleteProfileMutation.isPending}
      />
    </div>
  );
}

// ========================================
// MANIPULATION PROFILE CARD
// ========================================

interface ManipulationProfileCardProps {
  profile: Sip.ManipulationInfo;
  onView: () => void;
  onEdit: () => void;
  onTest: () => void;
  onToggle: () => void;
  onDelete: () => void;
  isToggling: boolean;
}

function ManipulationProfileCard({ 
  profile, 
  onView, 
  onEdit, 
  onTest,
  onToggle, 
  onDelete,
  isToggling 
}: ManipulationProfileCardProps) {
  const uniqueTargets = [...new Set(profile.rules.map(r => r.target))];
  const uniqueActions = [...new Set(profile.rules.map(r => r.action))];
  const enabledRules = profile.rules.filter(r => r.enabled !== false).length;

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg ${profile.enabled ? 'bg-blue-100' : 'bg-gray-100'}`}>
            <Wand2 className={`w-5 h-5 ${profile.enabled ? 'text-blue-600' : 'text-gray-400'}`} />
          </div>
          <div>
            <h3 className="font-medium text-gray-900">{profile.name}</h3>
            {profile.description && (
              <p className="text-sm text-gray-500 line-clamp-1">{profile.description}</p>
            )}
          </div>
        </div>
        <span className={`px-2 py-1 text-xs font-medium rounded-full border ${
          profile.enabled 
            ? 'bg-green-100 text-green-800 border-green-200'
            : 'bg-gray-100 text-gray-800 border-gray-200'
        }`}>
          {profile.enabled ? 'Enabled' : 'Disabled'}
        </span>
      </div>
      
      {/* Stats */}
      <div className="space-y-2 text-sm text-gray-600 mb-4">
        <div className="flex justify-between">
          <span>Rules:</span>
          <span className="font-medium">{enabledRules} / {profile.rules.length} active</span>
        </div>
      </div>

      {/* Target indicators */}
      <div className="mb-4">
        <p className="text-xs text-gray-500 mb-2">Targets:</p>
        <div className="flex flex-wrap gap-1">
          {uniqueTargets.map((target, idx) => (
            <span
              key={idx}
              className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getTargetColor(target)}`}
            >
              {target}
            </span>
          ))}
          {uniqueTargets.length === 0 && (
            <span className="text-xs text-gray-400">No targets defined</span>
          )}
        </div>
      </div>

      {/* Action indicators */}
      <div className="mb-4">
        <p className="text-xs text-gray-500 mb-2">Actions:</p>
        <div className="flex flex-wrap gap-1">
          {uniqueActions.map((action, idx) => (
            <span
              key={idx}
              className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getActionColor(action)}`}
            >
              {action}
            </span>
          ))}
          {uniqueActions.length === 0 && (
            <span className="text-xs text-gray-400">No actions defined</span>
          )}
        </div>
      </div>
      
      <div className="pt-4 border-t border-gray-100">
        <ItemActions
          variant="card"
          actions={[
            { kind: 'view', onClick: onView },
            { kind: 'test', onClick: onTest },
            { kind: 'edit', onClick: onEdit },
            {
              kind: profile.enabled ? 'disable' : 'enable',
              onClick: onToggle,
              disabled: isToggling,
            },
            { kind: 'delete', onClick: onDelete },
          ]}
        />
      </div>
    </div>
  );
}

// ========================================
// PROFILE DETAILS MODAL
// ========================================

interface ProfileDetailsModalProps {
  profile: Sip.ManipulationInfo;
  onClose: () => void;
  onEdit: () => void;
  onTest: () => void;
}

function ProfileDetailsModal({ profile, onClose, onEdit, onTest }: ProfileDetailsModalProps) {
  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-4xl w-full max-h-[80vh] flex flex-col">
        <div className="p-6 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${profile.enabled ? 'bg-blue-100' : 'bg-gray-100'}`}>
                <Wand2 className={`w-6 h-6 ${profile.enabled ? 'text-blue-600' : 'text-gray-400'}`} />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-gray-900">{profile.name}</h2>
                {profile.description && (
                  <p className="text-sm text-gray-600">{profile.description}</p>
                )}
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="p-6 overflow-y-auto flex-1">
          {/* Profile Info */}
          <div className="mb-6">
            <h3 className="text-lg font-medium text-gray-900 mb-3">Profile Details</h3>
            <div className="bg-gray-50 rounded-lg p-4 space-y-3">
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Profile ID</span>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-mono">{profile.id}</span>
                  <button
                    onClick={() => copyToClipboard(profile.id)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                </div>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Status</span>
                <span className={`text-sm font-medium ${profile.enabled ? 'text-green-600' : 'text-gray-600'}`}>
                  {profile.enabled ? 'Enabled' : 'Disabled'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Total Rules</span>
                <span className="text-sm text-gray-900">{profile.rules.length}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Created</span>
                <span className="text-sm text-gray-900">{new Date(profile.createdAt).toLocaleString()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Updated</span>
                <span className="text-sm text-gray-900">{new Date(profile.updatedAt).toLocaleString()}</span>
              </div>
            </div>
          </div>

          {/* Rules List */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-3">
              Rules ({profile.rules.length})
            </h3>
            {profile.rules.length === 0 ? (
              <div className="text-center py-8 bg-gray-50 rounded-lg">
                <p className="text-gray-500">No rules defined in this profile</p>
              </div>
            ) : (
              <div className="space-y-3">
                {profile.rules
                  .sort((a, b) => a.priority - b.priority)
                  .map((rule, idx) => (
                    <div
                      key={idx}
                      className={`bg-gray-50 rounded-lg p-4 ${rule.enabled === false ? 'opacity-50' : ''}`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-medium text-gray-500">#{rule.priority}</span>
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${getTargetColor(rule.target)}`}>
                            {rule.target}
                          </span>
                          <span className="text-xs text-gray-400">.</span>
                          <span className="text-xs font-medium text-gray-600">{rule.field}</span>
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${getActionColor(rule.action)}`}>
                            {rule.action}
                          </span>
                        </div>
                        {rule.enabled === false && (
                          <span className="text-xs text-gray-500">Disabled</span>
                        )}
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="text-gray-500">Pattern: </span>
                          <code className="font-mono text-xs bg-white px-1 py-0.5 rounded">{rule.pattern}</code>
                        </div>
                        <div>
                          <span className="text-gray-500">Value: </span>
                          <code className="font-mono text-xs bg-white px-1 py-0.5 rounded">{rule.value}</code>
                        </div>
                      </div>
                      
                      {rule.description && (
                        <p className="text-xs text-gray-500 mt-2">{rule.description}</p>
                      )}
                      
                      {rule.condition && (
                        <div className="mt-2 text-xs text-gray-500">
                          <span>Condition: </span>
                          <code className="font-mono bg-white px-1 py-0.5 rounded">
                            {rule.condition.target}.{rule.condition.field} ~ {rule.condition.pattern}
                          </code>
                        </div>
                      )}
                    </div>
                  ))}
              </div>
            )}
          </div>
        </div>

        <div className="p-6 border-t border-gray-200 bg-gray-50 flex-shrink-0">
          <div className="flex items-center justify-end gap-3">
            <button
              onClick={onTest}
              className="px-4 py-2 text-purple-700 bg-purple-50 border border-purple-200 rounded-lg hover:bg-purple-100 flex items-center gap-2"
            >
              <TestTube2 className="w-4 h-4" />
              Test Profile
            </button>
            <button
              onClick={onEdit}
              className="px-4 py-2 text-blue-700 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 flex items-center gap-2"
            >
              <Settings className="w-4 h-4" />
              Edit Profile
            </button>
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ========================================
// MANIPULATION USAGE INFO (for delete modal)
// ========================================

interface ManipulationUsageInfoProps {
  profileId: string;
  rulesCount: number;
}

function ManipulationUsageInfo({ profileId, rulesCount }: ManipulationUsageInfoProps) {
  const usageQuery = useSipManipulationUsage(profileId);
  const usage = usageQuery.data?.data;

  return (
    <>
      <p className="text-sm text-gray-600 mb-1 mt-2">Rules:</p>
      <p className="text-sm text-gray-700 mb-3">{rulesCount} rules</p>
      
      {usageQuery.isLoading ? (
        <div className="flex items-center text-sm text-gray-500">
          <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
          Checking usage...
        </div>
      ) : usage?.inUse ? (
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 mt-2">
          <div className="flex items-center text-orange-800">
            <AlertTriangle className="w-5 h-5 mr-2 flex-shrink-0" />
            <span className="font-medium">This profile is in use!</span>
          </div>
          <p className="text-sm text-orange-700 mt-1">
            Used by {usage.peerCount} peer(s) and {usage.ruleCount} routing rule(s).
            Deleting will remove these references.
          </p>
        </div>
      ) : null}
    </>
  );
}

export default SipManipulationsPage;
